import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';

const documentFieldsTabs = ['fields', 'conditionalTexts', 'details'] as const;

/** Tabs of the template modal's docked sidebar. */
export type DocumentFieldsTab = (typeof documentFieldsTabs)[number];

/** Search param holding the template the modal is open on. */
export const TEMPLATE_SEARCH_PARAM = 'template';
/** Search param holding which of the modal's sidebar tabs is showing. */
const TEMPLATE_TAB_SEARCH_PARAM = 'templateTab';

// Opening and closing the modal are navigations the user can undo, so they
// push; switching tabs inside it adjusts the modal in place and replaces.
const parseAsTemplateId = parseAsString.withOptions({ history: 'push' });
const parseAsTemplateTab = parseAsStringLiteral(documentFieldsTabs);

/**
 * The template a builder is editing lives in the URL rather than in component
 * state: the modal opens over whichever builder section is showing, so a
 * reload, a deep link, or browser Back all have to land back on the same
 * template and tab.
 */
export const useDocumentTemplateModal = () => {
  const [templateId, setTemplateId] = useQueryState(
    TEMPLATE_SEARCH_PARAM,
    parseAsTemplateId,
  );
  const [templateTab, setTemplateTab] = useQueryState(
    TEMPLATE_TAB_SEARCH_PARAM,
    parseAsTemplateTab,
  );

  const openTemplate = useCallback(
    (documentId: string) => {
      setTemplateId(documentId);
      setTemplateTab(null);
    },
    [setTemplateId, setTemplateTab],
  );

  const closeTemplate = useCallback(() => {
    setTemplateId(null);
    setTemplateTab(null);
  }, [setTemplateId, setTemplateTab]);

  const selectTab = useCallback(
    (tab: DocumentFieldsTab) =>
      setTemplateTab(tab, { history: 'replace', shallow: true }),
    [setTemplateTab],
  );

  return useMemo(
    () => ({
      templateId,
      templateTab,
      openTemplate,
      closeTemplate,
      selectTab,
    }),
    [closeTemplate, openTemplate, selectTab, templateId, templateTab],
  );
};
